import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Autocomplete,
  Divider,
  Link,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RegisterGoogleAutoComplete from "./RegisterGoogleAutoComplete";
import DuplicateEmailAlert from "../Utilities/DuplicateEmailAlert";
import RegisterFormColumnHeader from "./RegisterFormColumnHeader";
import RegisterUserTypeSelection from "./RegisterUserTypeSelection";
import RegisterGuestReferralSection from "./RegisterGuestReferralSection";
import { getLawFirmNoOptionsText } from "./register/registerLawFirmHelpers";
import {
  getRegisterStep1SubmitLabel,
  getRegisterStep2SubmitLabel,
} from "./register/registerSubmitLabels";
import { REGISTER_POSITION_OPTIONS } from "./register/registerFieldConfig";
import { registerFormColumnPShape } from "./register/registerFormColumnPropTypes";
import {
  primaryColor,
  secondaryColor,
  COMPONENT_HEIGHT,
  textFieldSx,
} from "../hooks/useFirstTimeRegister";

function renderLawFirmLocationDialogBody(p) {
  const {
    loadingLawFirmLocations,
    lawFirmLocations,
    lawFirmData,
    handleSelectLawFirmLocationFromTable,
  } = p;

  if (loadingLawFirmLocations) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">Loading locations...</Typography>
      </Box>
    );
  }

  if (lawFirmLocations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">No locations found for this law firm.</Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "0px" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>Address</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>City</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>State</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>Zip</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100", width: 100 }} align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lawFirmLocations.map((loc) => (
            <TableRow
              key={loc.value}
              hover
              selected={lawFirmData.lawFirmLocation === loc.value}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell>{loc.name || "—"}</TableCell>
              <TableCell>{loc.address || "—"}</TableCell>
              <TableCell>{loc.city || "—"}</TableCell>
              <TableCell>{loc.state || "—"}</TableCell>
              <TableCell>{loc.zip_code || "—"}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleSelectLawFirmLocationFromTable(loc)}
                  sx={{
                    borderRadius: "0px",
                    backgroundColor: primaryColor,
                    "&:hover": { backgroundColor: secondaryColor },
                  }}
                >
                  Select
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function renderLawFirmLocationDialog(p) {
  const { lawFirmLocationDialogOpen, setLawFirmLocationDialogOpen } = p;

  return (
    <Dialog
      open={lawFirmLocationDialogOpen}
      onClose={() => setLawFirmLocationDialogOpen(false)}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "0px" } }}}
    >
      <DialogTitle sx={{ fontWeight: 600, color: primaryColor }}>Select law firm location</DialogTitle>
      <DialogContent dividers>
        {renderLawFirmLocationDialogBody(p)}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => setLawFirmLocationDialogOpen(false)}
          sx={{ borderRadius: "0px", color: primaryColor }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function renderLawFirmLocationContent(p) {
  const {
    loadingLawFirmLocations,
    lawFirmLocations,
    selectedLawFirmLocation,
    setLawFirmLocationDialogOpen,
    errors,
  } = p;

  if (loadingLawFirmLocations) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">Loading locations...</Typography>
      </Box>
    );
  }
  if (lawFirmLocations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">No locations found for this law firm.</Typography>
    );
  }
  return (
    <>
      {selectedLawFirmLocation ? (
        <Box
          sx={{
            border: "2px solid",
            borderColor: "divider",
            borderRadius: "0px",
            p: 1.5,
            backgroundColor: "grey.50",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedLawFirmLocation.name || "—"}
            </Typography>
            <Button
              size="small"
              onClick={() => setLawFirmLocationDialogOpen(true)}
              sx={{
                p: 0,
                minWidth: "auto",
                borderRadius: "0px",
                color: primaryColor,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" }
              }}
            >
              Change location
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {[selectedLawFirmLocation.address, selectedLawFirmLocation.city, selectedLawFirmLocation.state, selectedLawFirmLocation.zip_code].filter(Boolean).join(", ")}
          </Typography>
        </Box>
      ) : (
        <Button
          variant="outlined"
          onClick={() => setLawFirmLocationDialogOpen(true)}
          data-testid="register-lawFirmLocation-select"
          sx={{
            height: COMPONENT_HEIGHT,
            borderRadius: "0px",
            borderColor: primaryColor,
            color: primaryColor,
            borderWidth: "2px",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { borderColor: secondaryColor, backgroundColor: "rgba(51, 51, 51, 0.08)", borderWidth: "2px" },
          }}
        >
          Select location
        </Button>
      )}
      {errors.lawFirmLocation && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>{errors.lawFirmLocation}</Typography>
      )}
    </>
  );
}

function renderLawFirmLocationPicker(p) {
  const {
    lawFirmData,
    errors,
  } = p;

  if (!lawFirmData.lawFirm) return null;

  return (
    <Box sx={{ mt: 0.85 }}>
      <FormControl component="fieldset" error={!!errors.lawFirmLocation} fullWidth>
        <FormLabel component="legend" sx={{ fontWeight: 600, color: primaryColor, mb: 0.5 }}>
          Law firm location
        </FormLabel>
        {renderLawFirmLocationContent(p)}
      </FormControl>
    </Box>
  );
}

function renderLawFirmSection(p) {
  const {
    userType,
    allLawFirms,
    filteredLawFirms,
    lawFirmData,
    errors,
    lawFirmSearch,
    loadingLawFirms,
    handleLawFirmAutocompleteChange,
    handleLawFirmSearchInputChange,
    handleOpenLawFirmDialog,
  } = p;

  if (userType !== "lawfirm_user" && userType !== "independent_paralegal") return null;

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mt: 0.75 }}>
        Law Firm Information
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography
          variant="body1"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          Search & Select Law Firm
        </Typography>
        <Tooltip title="Search for your law firm from our database" placement="top" arrow>
          <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
        <Autocomplete
          key={`law-firm-autocomplete-${allLawFirms.length}`}
          sx={{ flex: 1 }}
          options={filteredLawFirms}
          getOptionLabel={(option) => option.label}
          value={allLawFirms.find(firm => firm.value === lawFirmData.lawFirm) || null}
          onChange={handleLawFirmAutocompleteChange}
          onInputChange={handleLawFirmSearchInputChange}
          filterOptions={(x) => x} // Disable built-in filtering since we're doing it manually
          noOptionsText={getLawFirmNoOptionsText(lawFirmSearch, filteredLawFirms.length)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Type to search for law firms..."
              error={!!errors.lawFirm}
              helperText={errors.lawFirm}
              required
              inputProps={{ ...params.inputProps, 'data-testid': 'register-lawFirm' }}
              sx={{
                ...textFieldSx,
                "& .MuiOutlinedInput-root": {
                  ...textFieldSx["& .MuiOutlinedInput-root"],
                  "& fieldset": {
                    borderColor: errors.lawFirm ? "#d32f2f" : "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: errors.lawFirm ? "#d32f2f" : primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: errors.lawFirm ? "#d32f2f" : primaryColor,
                  },
                },
              }}
              slotProps={{ input: { ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loadingLawFirms ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}}
            />
          )}
        />
        <Button
          variant="outlined"
          onClick={handleOpenLawFirmDialog}
          data-testid="register-lawFirm-add"
          sx={{
            height: COMPONENT_HEIGHT,
            minWidth: COMPONENT_HEIGHT,
            borderRadius: "0px",
            borderColor: primaryColor,
            color: primaryColor,
            borderWidth: "2px",
            "&:hover": {
              borderColor: secondaryColor,
              backgroundColor: "rgba(51, 51, 51, 0.08)",
              borderWidth: "2px",
            },
          }}
        >
          <AddIcon />
        </Button>
      </Box>

      {renderLawFirmLocationPicker(p)}

      {renderLawFirmLocationDialog(p)}

      <Divider sx={{ my: 0.75 }} />
    </>
  );
}

function renderTypeSpecificContactInfo(p) {
  const {
    userType,
    formData,
    errors,
    handleInputChange,
    validateBusinessName,
    validatePhoneNumber,
    validateDateOfBirth,
    handleAddressCallback,
    validateAddress,
  } = p;

  if (
    !["professional_guardian", "individual_pg", "corporate_pg", "independent_paralegal"].includes(
      userType
    )
  ) {
    return null;
  }

  const isCorporatePg =
    userType === "corporate_pg" ||
    (userType === "professional_guardian" && formData.is_corporate_pg);

  return (
    <>
      <Divider sx={{ my: 0.75 }} />

      {isCorporatePg && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
              Business Name
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="business_name"
            value={formData.business_name}
            onChange={handleInputChange}
            onBlur={validateBusinessName}
            error={!!errors.business_name}
            helperText={errors.business_name}
            placeholder="Enter legal business name"
            required
            sx={textFieldSx}
            inputProps={{ 'data-testid': 'register-business_name' }}
          />
        </Box>
      )}


      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
              Phone Number
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            onBlur={validatePhoneNumber}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
            placeholder="(555) 555-5555"
            required
            sx={textFieldSx}
            inputProps={{ 'data-testid': 'register-phoneNumber' }}
          />
        </Box>
        {!isCorporatePg && (
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                Date of Birth
              </Typography>
            </Box>
            <TextField
              fullWidth
              type="date"
              variant="outlined"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              onBlur={validateDateOfBirth}
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth}
              slotProps={{ inputLabel: { shrink: true }}}
              required
              sx={textFieldSx}
              inputProps={{ 'data-testid': 'register-dateOfBirth' }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            Mailing Address
          </Typography>
        </Box>
        <RegisterGoogleAutoComplete
          placeholder="Search for your address..."
          callback={handleAddressCallback}
        />
        <TextField
          fullWidth
          variant="outlined"
          name="address"
          value={formData.address}
          onChange={handleInputChange}
          onBlur={validateAddress}
          error={!!errors.address}
          helperText={errors.address}
          placeholder="Mailing address"
          required
          sx={{ ...textFieldSx, mt: 0.5 }}
          inputProps={{ 'data-testid': 'register-address' }}
        />
      </Box>
    </>
  );
}

function renderPositionField(p) {
  const { userType, formData, errors, handlePositionAutocompleteChange } = p;

  if (userType !== "lawfirm_user") return null;

  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
          Position
        </Typography>
      </Box>
      <Autocomplete
        options={REGISTER_POSITION_OPTIONS}
        value={formData.position || null}
        onChange={handlePositionAutocompleteChange}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            error={!!errors.position}
            helperText={errors.position}
            placeholder="Select position"
            required
            sx={textFieldSx}
            inputProps={{ ...params.inputProps, 'data-testid': 'register-position' }}
          />
        )}
      />
    </Box>
  );
}

function renderStep1Fields(p) {
  const {
    registrationStep,
    formData,
    errors,
    handleInputChange,
    validateFirstName,
    validateLastName,
    handleAccountEmailBlur,
    duplicateEmailCheck,
    validateUsername,
    validatePassword,
    validateConfirmPassword,
    showPassword,
    showConfirmPassword,
    handleToggleShowPassword,
    handleToggleShowConfirmPassword,
  } = p;

  if (registrationStep !== 1) return null;

  return (
    <>
      {renderLawFirmSection(p)}

      {/* Personal Information */}
      <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mt: 0.75 }}>
        Personal Information
      </Typography>

      {/* Common Fields */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
              First Name
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            onBlur={validateFirstName}
            error={!!errors.firstName}
            helperText={errors.firstName}
            placeholder="First name"
            required
            sx={textFieldSx}
            inputProps={{ 'data-testid': 'register-firstName' }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
              Last Name
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            onBlur={validateLastName}
            error={!!errors.lastName}
            helperText={errors.lastName}
            placeholder="Last name"
            required
            sx={textFieldSx}
            inputProps={{ 'data-testid': 'register-lastName' }}
          />
        </Box>
      </Box>

      {renderTypeSpecificContactInfo(p)}

      {renderPositionField(p)}

      <Divider sx={{ my: 0.75 }} />

      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            Email Address
          </Typography>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          onBlur={handleAccountEmailBlur}
          error={!!errors.email}
          helperText={errors.email}
          placeholder="name@example.com"
          required
          sx={textFieldSx}
          inputProps={{ 'data-testid': 'register-email' }}
        />
        <DuplicateEmailAlert
          accounts={duplicateEmailCheck.existingAccounts}
          email={formData.email}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
              Username
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            onBlur={validateUsername}
            error={!!errors.username}
            helperText={errors.username}
            placeholder="Choose username"
            required
            sx={textFieldSx}
            inputProps={{ 'data-testid': 'register-username' }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
              Password
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleInputChange}
            onBlur={validatePassword}
            error={!!errors.password}
            helperText={errors.password}
            placeholder="Enter password"
            required
            sx={textFieldSx}
            inputProps={{ 'data-testid': 'register-password' }}
            slotProps={{ input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleToggleShowPassword} edge="end" sx={{ p: 0.5 }}>
                    {showPassword ? <VisibilityOff sx={{ fontSize: "1.2rem" }} /> : <Visibility sx={{ fontSize: "1.2rem" }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}}
          />
        </Box>
      </Box>

      <Box sx={{ mt: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            Confirm Password
          </Typography>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          name="confirmPassword"
          type={showConfirmPassword ? "text" : "password"}
          value={formData.confirmPassword}
          onChange={handleInputChange}
          onBlur={validateConfirmPassword}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          placeholder="Confirm password"
          required
          sx={textFieldSx}
          inputProps={{ 'data-testid': 'register-confirmPassword' }}
          slotProps={{ input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleToggleShowConfirmPassword} edge="end" sx={{ p: 0.5 }}>
                  {showConfirmPassword ? <VisibilityOff sx={{ fontSize: "1.2rem" }} /> : <Visibility sx={{ fontSize: "1.2rem" }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}}
        />
      </Box>
    </>
  );
}

function renderCorporateContactRow(c, idx, handleRemoveCorporateContact) {
  return (
    <Box
      key={c.email ? `pg-contact-${c.email}` : `pg-contact-${idx}-${String(c.full_name)}`}
      sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, border: "1px solid #E0E0E0", borderRadius: "4px", mb: 0.5 }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>{c.full_name}</Typography>
        <Typography variant="caption" color="text.secondary">{c.email} {c.is_admin ? "(Admin)" : ""}</Typography>
      </Box>
      <IconButton size="small" onClick={() => handleRemoveCorporateContact(idx)}>
        <ArrowBackIcon sx={{ fontSize: "1rem", transform: "rotate(0deg)" }} />
      </IconButton>
    </Box>
  );
}

function renderStep2CorporateContacts(p) {
  const {
    registrationStep,
    userType,
    formData,
    corporateContacts,
    contactForm,
    setContactForm,
    contactErrors,
    formatPhoneNumber,
    handleRemoveCorporateContact,
    handleAddCorporateContact,
  } = p;

  if (
    !(
      registrationStep === 2 &&
      (userType === "corporate_pg" ||
        (userType === "professional_guardian" && formData.is_corporate_pg))
    )
  ) {
    return null;
  }

  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
        Add Team Members (Optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Add contact users who can access this account. You can also do this later from your dashboard.
      </Typography>

      {corporateContacts.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {corporateContacts.map((c, idx) => renderCorporateContactRow(c, idx, handleRemoveCorporateContact))}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 0.5 }}>
        <TextField fullWidth placeholder="Full Name" value={contactForm.full_name} onChange={(e) => setContactForm(prev => ({ ...prev, full_name: e.target.value }))} error={!!contactErrors.full_name} helperText={contactErrors.full_name} slotProps={{ htmlInput: { "data-testid": "register-corporateContact-fullName" } }} sx={textFieldSx} />
        <TextField fullWidth placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))} error={!!contactErrors.email} helperText={contactErrors.email} slotProps={{ htmlInput: { "data-testid": "register-corporateContact-email" } }} sx={textFieldSx} />
      </Box>
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mt: 0.5 }}>
        <TextField fullWidth placeholder="Phone (optional)" value={contactForm.phone_number} onChange={(e) => setContactForm(prev => ({ ...prev, phone_number: formatPhoneNumber(e.target.value) }))} slotProps={{ htmlInput: { "data-testid": "register-corporateContact-phone" } }} sx={textFieldSx} />
        <FormControlLabel
          control={<Checkbox checked={contactForm.is_admin} onChange={(e) => setContactForm(prev => ({ ...prev, is_admin: e.target.checked }))} size="small" slotProps={{ input: { "data-testid": "register-corporateContact-isAdmin" } }} />}
          label={<Typography variant="body2">Admin</Typography>}
          sx={{ minWidth: "90px" }}
        />
      </Box>
      <Button
        variant="outlined"
        onClick={handleAddCorporateContact}
        data-testid="register-corporateContact-add"
        sx={{ mt: 0.5, borderRadius: "0px", borderColor: primaryColor, color: primaryColor, textTransform: "none", fontWeight: 600, height: COMPONENT_HEIGHT }}
      >
        + Add Contact
      </Button>
    </>
  );
}

function renderStep2SubmitActions(p) {
  const { loading, duplicateEmailCheck, handleRegister, corporateContacts, setRegistrationStep } = p;

  return (
    <>
      <Button
        variant="contained"
        disabled={loading || duplicateEmailCheck.hasDuplicate}
        onClick={handleRegister}
        data-testid="register-submit"
        sx={{
          borderRadius: "0px",
          py: 1.2,
          fontSize: "1rem",
          fontWeight: 600,
          bgcolor: primaryColor,
          color: "white",
          boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
          "&:hover": {
            bgcolor: secondaryColor,
            boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
            transform: "translateY(-1px)",
          },
          textTransform: "none",
          transition: "all 0.3s ease",
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : getRegisterStep2SubmitLabel(corporateContacts.length)}
      </Button>
      <Button
        variant="outlined"
        onClick={() => setRegistrationStep(1)}
        disabled={loading}
        sx={{
          borderRadius: "0px",
          py: 1.2,
          fontSize: "1rem",
          fontWeight: 600,
          borderColor: primaryColor,
          color: primaryColor,
          borderWidth: "2px",
          "&:hover": {
            borderColor: secondaryColor,
            backgroundColor: "rgba(51, 51, 51, 0.08)",
            borderWidth: "2px",
            transform: "translateY(-1px)",
          },
          textTransform: "none",
          transition: "all 0.3s ease",
        }}
      >
        Back
      </Button>
    </>
  );
}

function renderStep1SubmitActions(p) {
  const { loading, duplicateEmailCheck, handleRegister, userType, formData, handleFormSecondaryBack } = p;

  return (
    <>
      <Button
        variant="contained"
        onClick={handleRegister}
        disabled={loading || duplicateEmailCheck.hasDuplicate}
        data-testid="register-submit"
        sx={{
          borderRadius: "0px",
          py: 1.2,
          fontSize: "1rem",
          fontWeight: 600,
          bgcolor: primaryColor,
          color: "white",
          boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
          "&:hover": {
            bgcolor: secondaryColor,
            boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
            transform: "translateY(-1px)",
          },
          textTransform: "none",
          transition: "all 0.3s ease",
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : getRegisterStep1SubmitLabel(userType, formData)}
      </Button>

      <Button
        variant="outlined"
        onClick={handleFormSecondaryBack}
        sx={{
          borderRadius: "0px",
          py: 1.2,
          fontSize: "1rem",
          fontWeight: 600,
          borderColor: primaryColor,
          color: primaryColor,
          borderWidth: "2px",
          "&:hover": {
            borderColor: secondaryColor,
            backgroundColor: "rgba(51, 51, 51, 0.08)",
            borderWidth: "2px",
            transform: "translateY(-1px)",
          },
          textTransform: "none",
          transition: "all 0.3s ease",
        }}
      >
        {userType ? "Back to Selection" : "Back to Login"}
      </Button>
    </>
  );
}

function renderSubmitButtons(p) {
  const { registrationStep, handleSignInLink } = p;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
      {registrationStep === 2 ? renderStep2SubmitActions(p) : renderStep1SubmitActions(p)}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Divider sx={{ flex: 1 }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>or</Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>

      <Box sx={{ textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Already have an account?{" "}
          <Link
            href="#"
            onClick={handleSignInLink}
            sx={{
              fontWeight: 600,
              color: primaryColor,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Sign In
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

function renderUserTypeSelection(userType, onUserTypeRadioChange) {
  if (userType) return null;

  return (
    <RegisterUserTypeSelection
      userType={userType}
      onUserTypeRadioChange={onUserTypeRadioChange}
    />
  );
}

function renderGuestReferralSection(p) {
  const {
    userType,
    guestReferralSource,
    setGuestReferralSource,
    guestReferralName,
    setGuestReferralName,
    handleNavigateGuestBond,
    handleGuestBackToSelection,
  } = p;

  if (userType !== "individual_bond_request") return null;

  return (
    <RegisterGuestReferralSection
      guestReferralSource={guestReferralSource}
      setGuestReferralSource={setGuestReferralSource}
      guestReferralName={guestReferralName}
      setGuestReferralName={setGuestReferralName}
      onNavigateGuestBond={handleNavigateGuestBond}
      onGuestBackToSelection={handleGuestBackToSelection}
    />
  );
}

function renderRegistrationForm(p) {
  const { userType } = p;

  if (!userType || userType === "individual_bond_request") return null;

  return (
    <Box
      component="form"
      data-testid="register-form"
      sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
    >
      {renderStep1Fields(p)}
      {renderStep2CorporateContacts(p)}
      {renderSubmitButtons(p)}
    </Box>
  );
}

const RegisterFormColumn = ({ p }) => {
  const { userType, handleHeaderBackClick, handleUserTypeRadioChange } = p;

  return (
    <Box
      sx={{
        width: "90%",
        maxWidth: "480px",
        backgroundColor: "transparent",
        p: { xs: 3, sm: 4 },
        pb: 20,
        position: "relative",
        my: "auto",
      }}
    >
      <RegisterFormColumnHeader onBackClick={handleHeaderBackClick} />

      {renderUserTypeSelection(userType, handleUserTypeRadioChange)}

      {renderGuestReferralSection(p)}

      {/* Registration Form */}
      {renderRegistrationForm(p)}
    </Box>
  );
};

RegisterFormColumn.propTypes = {
  p: PropTypes.shape(registerFormColumnPShape).isRequired,
};

export default RegisterFormColumn;
